import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $getSelection, $isRangeSelection, $isRootOrShadowRoot, COMMAND_PRIORITY_CRITICAL, SELECTION_CHANGE_COMMAND } from 'lexical';
import { FC, useCallback, useEffect, useState } from 'react';

import FormatListButtons from './FormatListButtons';
import { BlockType } from './FormatListButtons/types';
import FormatTextButtons from './FormatTextButtons';
import { FormatType } from './FormatTextButtons/types';
import HistoryButtons from './HistoryButtons';

const allowedBlockTypes: BlockType[] = ['paragraph', 'bullet', 'number', 'check'];

const Divider: FC = () => <div className="divider" />;

export const ToolbarPlugin: FC = () => {
    const [editor] = useLexicalComposerContext();
    const [activeEditor, setActiveEditor] = useState(editor);
    const [isEditable, setIsEditable] = useState(() => editor.isEditable());

    const [blockType, setBlockType] = useState<BlockType>('paragraph');
    const [formatType, setFormatType] = useState<FormatType>({
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false
    });

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, e => {
                          const parent = e.getParent();
                          return parent !== null && $isRootOrShadowRoot(parent);
                      });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow();
            }

            const elementKey = element.getKey();
            const elementDOM = activeEditor.getElementByKey(elementKey);

            // Update text format
            setFormatType({
                bold: selection.hasFormat('bold'),
                italic: selection.hasFormat('italic'),
                underline: selection.hasFormat('underline'),
                strikethrough: selection.hasFormat('strikethrough')
            });

            // Update List
            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
                    const type = parentList ? parentList.getListType() : element.getListType();
                    setBlockType(type);
                } else {
                    const type = $isHeadingNode(element) ? element.getTag() : element.getType();
                    if (type in allowedBlockTypes) {
                        setBlockType(type as typeof allowedBlockTypes[number]);
                    }
                }
            }
        }
    }, [activeEditor]);

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                updateToolbar();
                setActiveEditor(newEditor);
                return false;
            },
            COMMAND_PRIORITY_CRITICAL
        );
    }, [editor, updateToolbar]);

    useEffect(() => {
        return mergeRegister(
            editor.registerEditableListener(editable => setIsEditable(editable)),
            activeEditor.registerUpdateListener(({ editorState }) => editorState.read(() => updateToolbar()))
        );
    }, [activeEditor, editor, updateToolbar]);

    return (
        <div className="toolbar">
            <>
                <HistoryButtons isEditable={isEditable} activeEditor={activeEditor} />
                <Divider />
                <FormatTextButtons isEditable={isEditable} formatType={formatType} activeEditor={activeEditor} />
                <Divider />
                <FormatListButtons isEditable={isEditable} blockType={blockType} onBlockTypeChange={setBlockType} />
            </>
        </div>
    );
};
